/**
 * Frontend Validation Schemas for Profile Management
 * Uses Zod for client-side validation before API calls
 *
 * Use the factory functions (getProfileUpdateSchema, getPasswordChangeSchema)
 * for runtime validation with translated error messages.
 */

import { z } from 'zod';
import type { TFunction } from 'i18next';
import { validateUSPhoneNumber } from '@/utils/phoneNumber';

/**
 * Factory: Profile Update Schema with translated error messages
 */
export function getProfileUpdateSchema(t: TFunction) {
  return z.object({
    firstName: z
      .string()
      .min(1, t('profile.validation.firstNameRequired'))
      .max(100, t('profile.validation.firstNameMaxLength'))
      .optional(),
    lastName: z
      .string()
      .min(1, t('profile.validation.lastNameRequired'))
      .max(100, t('profile.validation.lastNameMaxLength'))
      .optional(),
    phoneNumber: z
      .string()
      .optional()
      .refine((val) => !val || validateUSPhoneNumber(val), {
        message: t('profile.validation.invalidPhone'),
      }),
  });
}

/**
 * Factory: Password Change Schema with translated error messages
 * Note: The `t` function must have access to both 'parent' and 'common' namespaces
 * (i.e., caller should use `useTranslation(['parent', 'common'])`)
 */
export function getPasswordChangeSchema(t: TFunction) {
  return z
    .object({
      currentPassword: z.string().min(1, t('profile.validation.currentPasswordRequired')),
      newPassword: z
        .string()
        .min(8, t('common:auth.password.tooShort'))
        .max(128, t('common:auth.password.tooLong'))
        .refine((val) => /[a-zA-Z]/.test(val) && /\d/.test(val), {
          message: t('common:auth.password.needsLetterAndNumber'),
        }),
      confirmPassword: z.string().min(1, t('profile.validation.confirmPasswordRequired')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('common:auth.password.doesNotMatch'),
      path: ['confirmPassword'],
    });
}

export type ProfileUpdateFormData = z.infer<ReturnType<typeof getProfileUpdateSchema>>;
export type PasswordChangeFormData = z.infer<ReturnType<typeof getPasswordChangeSchema>>;
